/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { RootState } from "store/rootReducer";
import styled from "styled-components";

import { useUserUnit } from "../../hooks";
import { MlFeature, MlTarget, NormalizeFeaturePackage } from "./types";
import { getNormalizedByLabel } from "./utils/nameUtils";

interface SingularValueDetailTableProps {
  getFeatureDisplayName?: (feature: MlFeature) => string;
}

const SingularValueDetailTable: FC<SingularValueDetailTableProps> = ({
  getFeatureDisplayName
}) => {
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const unitType = useUserUnit();
  const mlState = useSelector((state: RootState) => state.ml);
  const categoryList = useSelector((state: RootState) => state.groupBy.categoryList);
  const featuresWithSingularValue = mlState.warnDetails?.featuresWithSingleValue || {};
  const inputFeatures: MlFeature[] = (mlState.inputFeatures as MlFeature[]) || [];
  const targetFeature: MlTarget = mlState.targetFeature as MlTarget;
  const normalizationSummary = mlState.warnDetails?.normalizationSummary ?? [];

  const normalizationLookup = useMemo(() => {
    const map = new Map<string, NormalizeFeaturePackage>();
    normalizationSummary.forEach((item) => {
      if (item?.ApplyTo?.Property) {
        map.set(item.ApplyTo.Property, item);
      }
    });
    return map;
  }, [normalizationSummary]);

  const columns: ColumnsType<any> = useMemo(() => {
    return [
      {
        title: "Feature",
        dataIndex: "feature",
        key: "feature",
        ellipsis: true,
        width: "60%",
        render: (_: string, record: any) => (
          <FeatureNameColumn>
            <FeatureName>{record.feature}</FeatureName>
            {record.normalizedBy ? (
              <NormalizedByLabel>{record.normalizedBy}</NormalizedByLabel>
            ) : null}
          </FeatureNameColumn>
        )
      },
      {
        title: "Value",
        dataIndex: "value",
        key: "value",
        ellipsis: true,
        width: "40%",
        render: (value: any) => {
          if (value === null || value === undefined) {
            return <NAValue>n/a</NAValue>;
          }
          if (typeof value === "object") {
            return JSON.stringify(value);
          }
          return String(value);
        }
      }
    ];
  }, []);

  const dataSource = useMemo(() => {
    if (
      !featuresWithSingularValue ||
      Object.keys(featuresWithSingularValue).length === 0
    ) {
      return [];
    }
    return Object.entries(featuresWithSingularValue)
      .map(([key, value], idx) => {
        const feature =
          inputFeatures.find((f) => f.Property === key) ||
          (targetFeature?.Property === key ? targetFeature : null);

        const displayName = getFeatureDisplayName
          ? getFeatureDisplayName(feature as MlFeature)
          : feature?.NiceName || key;

        const normEntry = normalizationLookup.get(key);
        const normalizedBy = getNormalizedByLabel(
          normEntry?.NormalizeBy,
          unitType,
          categoryList
        );

        return {
          key: idx,
          feature: displayName,
          normalizedBy,
          value: value
        };
      })
      .filter((item) => item.feature !== null && item.feature !== "");
  }, [
    featuresWithSingularValue,
    inputFeatures,
    targetFeature,
    getFeatureDisplayName,
    normalizationLookup,
    unitType,
    categoryList
  ]);

  return (
    <Container>
      <HeaderSection>
        <TargetFeatureName>
          {targetFeature?.NiceName || "Features with Singular Values"}
        </TargetFeatureName>
        <InfoText>
          Total features with singular values:{" "}
          {Object.keys(featuresWithSingularValue).length}
          <br />
          These features have only one unique value across all valid wells and will not
          provide meaningful calculations.
        </InfoText>
      </HeaderSection>
      <TableContainer>
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions: ["25", "50", "100", "200"],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            }
          }}
          sticky
          size="small"
          bordered
          scroll={{ x: "max-content", y: "calc(70vh - 220px)" }}
        />
      </TableContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  font-size: 14px;
`;

const HeaderSection = styled.div`
  flex-shrink: 0;
  margin-bottom: 8px;
`;

const TargetFeatureName = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 4px;
`;

const InfoText = styled.div`
  font-size: 14px;
`;

const TableContainer = styled.div`
  flex: 1;
  min-height: 0;
  .ant-table-pagination.ant-pagination {
    margin: 16px 0 0;
  }
  .ant-table-body {
    overflow: auto !important;
  }
`;

const NAValue = styled.span`
  color: var(--color-danger);
  font-weight: bold;
`;

const FeatureNameColumn = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
`;

const FeatureName = styled.span`
  font-weight: 500;
`;

const NormalizedByLabel = styled.span`
  font-size: 12px;
  color: var(--color-text-60);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
`;

export default SingularValueDetailTable;
